import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon, AlertTriangleIcon } from 'lucide-react';
import { Budget, BudgetType } from './BudgetManagement';
import { OrgUnitSelector } from '../../settings/orgunit/OrgUnitSelector';
import { type OrgUnit } from '../sharedFinancialData';
import { GLCodeInput } from './GLCodeInput';
import { RuleEditor } from './RuleEditor';
import { CurrencySelector } from '../../settings/CurrencySelector';
interface BudgetFormProps {
  budget?: Budget;
  budgetTypes: BudgetType[];
  onSave: (budget: Budget | Omit<Budget, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
  locale: string;
}
export function BudgetForm({
  budget,
  budgetTypes,
  onSave,
  onCancel,
  locale
}: BudgetFormProps) {
  const isEditing = !!budget;
  const [formData, setFormData] = useState<Omit<Budget, 'id' | 'createdAt' | 'updatedAt' | 'budgetTypeName'> & {
    budgetTypeName?: string;
  }>({
    name: '',
    description: '',
    budgetTypeId: '',
    glCode: '',
    scope: 'Global',
    orgUnitId: undefined,
    orgUnitName: undefined,
    orgUnitPath: undefined,
    totalAmount: 0,
    remainingAmount: 0,
    currency: 'USD',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 3, new Date().getDate()).toISOString().split('T')[0],
    status: 'Active',
    rules: '{}'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [orgUnit, setOrgUnit] = useState<OrgUnit | null>(null);
  useEffect(() => {
    if (budget) {
      setFormData({
        name: budget.name,
        description: budget.description,
        budgetTypeId: budget.budgetTypeId,
        budgetTypeName: budget.budgetTypeName,
        glCode: budget.glCode,
        scope: budget.scope,
        orgUnitId: budget.orgUnitId,
        orgUnitName: budget.orgUnitName,
        orgUnitPath: budget.orgUnitPath,
        totalAmount: budget.totalAmount,
        remainingAmount: budget.remainingAmount,
        currency: budget.currency,
        startDate: budget.startDate,
        endDate: budget.endDate,
        status: budget.status,
        rules: budget.rules
      });
    }
  }, [budget]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    if (name === 'scope' && value === 'Global') {
      setFormData({
        ...formData,
        scope: value as 'Global' | 'OrgUnit',
        orgUnitId: undefined,
        orgUnitName: undefined,
        orgUnitPath: undefined
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: undefined
      });
    }
  };
  const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: parseFloat(value) || 0
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: undefined
      });
    }
  };
  const handleBudgetTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedTypeId = e.target.value;
    const selectedType = budgetTypes.find(type => type.id === selectedTypeId);
    setFormData({
      ...formData,
      budgetTypeId: selectedTypeId,
      budgetTypeName: selectedType?.name || ''
    });
    // Clear error when field is edited
    if (errors.budgetTypeId) {
      setErrors({
        ...errors,
        budgetTypeId: undefined
      });
    }
  };
  const handleGLCodeChange = (glCode: string) => {
    setFormData({
      ...formData,
      glCode
    });
    // Clear error when field is edited
    if (errors.glCode) {
      setErrors({
        ...errors,
        glCode: undefined
      });
    }
  };
  const handleRulesChange = (rulesJson: string) => {
    setFormData({
      ...formData,
      rules: rulesJson
    });
  };
  const handleCurrencyChange = (currency: string) => {
    setFormData({
      ...formData,
      currency
    });
  };
  const handleOrgUnitChange = (orgUnitId: string, selectedOrgUnit: OrgUnit | null) => {
    setOrgUnit(selectedOrgUnit);
    setFormData({
      ...formData,
      orgUnitId: orgUnitId || undefined,
      orgUnitName: selectedOrgUnit?.name,
      orgUnitPath: selectedOrgUnit?.path
    });
    // Clear errors
    if (errors.orgUnitId) {
      setErrors({
        ...errors,
        orgUnitId: undefined
      });
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Budget name is required';
    }
    if (!formData.budgetTypeId) {
      newErrors.budgetTypeId = 'Budget type is required';
    }
    if (!formData.glCode.trim()) {
      newErrors.glCode = 'GL Code is required';
    }
    if (formData.scope === 'OrgUnit' && !formData.orgUnitId) {
      newErrors.orgUnitId = 'Organisation unit is required when scope is OrgUnit';
    }
    if (formData.totalAmount <= 0) {
      newErrors.totalAmount = 'Total amount must be greater than zero';
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }
    if (formData.startDate && formData.endDate && new Date(formData.startDate) > new Date(formData.endDate)) {
      newErrors.endDate = 'End date must be after start date';
    }
    try {
      JSON.parse(formData.rules);
    } catch (e) {
      newErrors.rules = 'Rules must be valid JSON';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // If creating a new budget, set the remaining amount equal to total amount
      const finalData = isEditing ? formData : {
        ...formData,
        remainingAmount: formData.totalAmount
      };
      onSave(finalData as Budget);
    }
  };
  return <form onSubmit={handleSubmit} className="space-y-8" data-id="element-8611">
      <div className="flex justify-between items-center mb-6" data-id="element-8612">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-8613">
          {isEditing ? 'Edit Budget' : 'Create New Budget'}
        </h3>
        <div className="flex space-x-3" data-id="element-8614">
          <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8615">
            <XIcon className="h-4 w-4 mr-2" data-id="element-8616" />
            Cancel
          </button>
          <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8617">
            <SaveIcon className="h-4 w-4 mr-2" data-id="element-8618" />
            {isEditing ? 'Update Budget' : 'Create Budget'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-8619">
        <div className="sm:col-span-6" data-id="element-8620">
          <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-8621">
            General Information
          </h4>
          <div className="bg-white shadow overflow-hidden sm:rounded-md p-6" data-id="element-8622">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-8623">
              <div className="sm:col-span-4" data-id="element-8624">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-8625">
                  Budget Name *
                </label>
                <div className="mt-1" data-id="element-8626">
                  <input type="text" name="name" id="name" value={formData.name} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.name ? 'border-red-300' : ''}`} data-id="element-8627" />
                  {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-8628">{errors.name}</p>}
                </div>
              </div>

              <div className="sm:col-span-2" data-id="element-8629">
                <label htmlFor="budgetTypeId" className="block text-sm font-medium text-gray-700" data-id="element-8630">
                  Budget Type *
                </label>
                <div className="mt-1" data-id="element-8631">
                  <select id="budgetTypeId" name="budgetTypeId" value={formData.budgetTypeId} onChange={handleBudgetTypeChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.budgetTypeId ? 'border-red-300' : ''}`} data-id="element-8632">
                    <option value="" data-id="element-8633">Select Budget Type</option>
                    {budgetTypes.map(type => <option key={type.id} value={type.id} data-id="element-8634">
                        {type.name}
                      </option>)}
                  </select>
                  {errors.budgetTypeId && <p className="mt-1 text-sm text-red-600" data-id="element-8635">
                      {errors.budgetTypeId}
                    </p>}
                </div>
              </div>

              <div className="sm:col-span-6" data-id="element-8636">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-8637">
                  Description
                </label>
                <div className="mt-1" data-id="element-8638">
                  <textarea id="description" name="description" rows={3} value={formData.description} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8639" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="sm:col-span-6" data-id="element-8640">
          <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-8641">
            Budget Scope
          </h4>
          <div className="bg-white shadow overflow-hidden sm:rounded-md p-6" data-id="element-8642">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-8643">
              {/* Scope Selector */}
              <div className="sm:col-span-3" data-id="element-8644">
                <label htmlFor="scope" className="block text-sm font-medium text-gray-700" data-id="element-8645">
                  Scope <span className="text-red-500" data-id="element-8646">*</span>
                </label>
                <div className="mt-1" data-id="element-8647">
                  <select id="scope" name="scope" value={formData.scope} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8648">
                    <option value="Global" data-id="element-8649">Global</option>
                    <option value="OrgUnit" data-id="element-8650">Organisation Unit</option>
                  </select>
                </div>
                <p className="mt-1 text-xs text-gray-500" data-id="element-8651">
                  {formData.scope === 'Global' ? 'Available to all organisation units' : 'Specific to selected organisation unit'}
                </p>
              </div>

              {/* OrgUnit Selector */}
              {formData.scope === 'OrgUnit' && <div className="sm:col-span-3" data-id="element-8652">
                  <OrgUnitSelector value={formData.orgUnitId || ''} onChange={handleOrgUnitChange} label="Organisation Unit" placeholder="Select organisation unit" required={true} error={errors.orgUnitId} showPath={true} data-id="element-8653" />
                </div>}

              <div className="sm:col-span-3" data-id="element-8654">
                <label htmlFor="glCode" className="block text-sm font-medium text-gray-700" data-id="element-8655">
                  GL Code *
                </label>
                <div className="mt-1" data-id="element-8656">
                  <GLCodeInput value={formData.glCode} onChange={handleGLCodeChange} error={errors.glCode} data-id="element-8657" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="sm:col-span-6" data-id="element-8658">
          <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-8659">
            Amount & Period
          </h4>
          <div className="bg-white shadow overflow-hidden sm:rounded-md p-6" data-id="element-8660">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-8661">
              <div className="sm:col-span-2" data-id="element-8662">
                <label htmlFor="totalAmount" className="block text-sm font-medium text-gray-700" data-id="element-8663">
                  Total Amount *
                </label>
                <div className="mt-1 relative rounded-md shadow-sm" data-id="element-8664">
                  <input type="number" name="totalAmount" id="totalAmount" min="0" step="0.01" value={formData.totalAmount} onChange={handleNumberInputChange} className={`focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md ${errors.totalAmount ? 'border-red-300' : ''}`} data-id="element-8665" />
                  <div className="absolute inset-y-0 right-0 flex items-center" data-id="element-8666">
                    <div className="h-full rounded-md border-transparent bg-transparent py-0 pl-2 pr-7 text-gray-500 sm:text-sm" data-id="element-8667">
                      {formData.currency}
                    </div>
                  </div>
                </div>
                {errors.totalAmount && <p className="mt-1 text-sm text-red-600" data-id="element-8668">
                    {errors.totalAmount}
                  </p>}
              </div>

              <div className="sm:col-span-2" data-id="element-8669">
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-8670">
                  Currency
                </label>
                <div className="mt-1" data-id="element-8671">
                  <CurrencySelector value={formData.currency} onChange={handleCurrencyChange} countryCode="" locale={locale} data-id="element-8672" />
                </div>
              </div>

              {isEditing && <div className="sm:col-span-2" data-id="element-8673">
                  <label htmlFor="remainingAmount" className="block text-sm font-medium text-gray-700" data-id="element-8674">
                    Remaining Amount
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm" data-id="element-8675">
                    <input type="number" name="remainingAmount" id="remainingAmount" min="0" step="0.01" value={formData.remainingAmount} onChange={handleNumberInputChange} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md" data-id="element-8676" />
                    <div className="absolute inset-y-0 right-0 flex items-center" data-id="element-8677">
                      <div className="h-full rounded-md border-transparent bg-transparent py-0 pl-2 pr-7 text-gray-500 sm:text-sm" data-id="element-8678">
                        {formData.currency}
                      </div>
                    </div>
                  </div>
                </div>}

              <div className="sm:col-span-3" data-id="element-8679">
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700" data-id="element-8680">
                  Start Date *
                </label>
                <div className="mt-1" data-id="element-8681">
                  <input type="date" name="startDate" id="startDate" value={formData.startDate} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.startDate ? 'border-red-300' : ''}`} data-id="element-8682" />
                  {errors.startDate && <p className="mt-1 text-sm text-red-600" data-id="element-8683">
                      {errors.startDate}
                    </p>}
                </div>
              </div>

              <div className="sm:col-span-3" data-id="element-8684">
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700" data-id="element-8685">
                  End Date *
                </label>
                <div className="mt-1" data-id="element-8686">
                  <input type="date" name="endDate" id="endDate" value={formData.endDate} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.endDate ? 'border-red-300' : ''}`} data-id="element-8687" />
                  {errors.endDate && <p className="mt-1 text-sm text-red-600" data-id="element-8688">
                      {errors.endDate}
                    </p>}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="sm:col-span-6" data-id="element-8689">
          <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-8690">
            Budget Rules
          </h4>
          <div className="bg-white shadow overflow-hidden sm:rounded-md p-6" data-id="element-8691">
            <RuleEditor value={formData.rules} onChange={handleRulesChange} error={errors.rules} data-id="element-8692" />
          </div>
        </div>

        {isEditing && <div className="sm:col-span-6" data-id="element-8693">
            <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-8694">
              Lifecycle
            </h4>
            <div className="bg-white shadow overflow-hidden sm:rounded-md p-6" data-id="element-8695">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-8696">
                <div className="sm:col-span-3" data-id="element-8697">
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-8698">
                    Status
                  </label>
                  <div className="mt-1" data-id="element-8699">
                    <select id="status" name="status" value={formData.status} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8700">
                      <option value="Active" data-id="element-8701">Active</option>
                      <option value="Archived" data-id="element-8702">Archived</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>
    </form>;
}